/**
 * Dynamic Favicon Loader
 * Loads favicon settings from CMS and updates the HTML <head>
 */

import { BASE_DOMAIN } from './apiConfig';

interface FaviconSettings {
  faviconUrl: string;
  faviconType: 'svg' | 'png' | 'ico';
  appleIconUrl: string;
  themeColor: string;
}

/**
 * Load favicon settings from CMS and apply them to the document
 */
export async function loadDynamicFavicon(): Promise<void> {
  try {
    // Get auth token from localStorage
    const token = localStorage.getItem('cmsAuthToken');
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if token exists
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${BASE_DOMAIN}/api/make-server-e799d0d8/cms/favicon`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      console.warn('Failed to load favicon settings from CMS, using defaults');
      return;
    }

    const data = await response.json();
    const settings: FaviconSettings = data.settings;

    if (!settings) {
      console.warn('No favicon settings found in CMS response');
      return;
    }

    // Update or create main favicon link
    updateOrCreateLink('icon', settings.faviconUrl, `image/${settings.faviconType}`);

    // Update or create alternate icon (for PNG fallback)
    if (settings.faviconType === 'svg') {
      updateOrCreateLink('alternate icon', settings.faviconUrl, 'image/png');
    }

    // Update or create Apple touch icon
    updateOrCreateLink('apple-touch-icon', settings.appleIconUrl);

    // Update or create theme color meta tag
    updateOrCreateMetaTag('theme-color', settings.themeColor);

    console.log('✅ Dynamic favicon loaded from CMS');
  } catch (error) {
    console.error('Error loading dynamic favicon:', error);
    // Fail silently - keep default favicon
  }
}

/**
 * Update existing link tag or create new one
 */
function updateOrCreateLink(rel: string, href: string, type?: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  link.href = href;
  if (type) {
    link.type = type;
  }
}

/**
 * Update existing meta tag or create new one
 */
function updateOrCreateMetaTag(name: string, content: string): void {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  
  meta.content = content;
}